import React, { useEffect, useState } from "react";
import "./FormProfile.css";
import { useFormik } from "formik";
import * as yup from "yup";
import { TextInput } from "../../components/inputs/TextInput/TextInput";
import { InputAutoComplete } from "../../components/InputAutoComplete/InputAutoComplete";
import ButtonSecondary from "../../components/Buttons/ButtonSecondary/ButtonSecondary";
import iconAddPhoto from "../../assets/icon-camera.svg";
import api from "../../utils/api";
import unmask from "../../utils/unmask";
import { toast } from "react-toastify";
import axios from "axios";
import { getBase64 } from '../../utils/getBase64';
import CompanyAvatar from "../../components/CompanyAvatar/CompanyAvatar";

const FormProfile = ({ company, updateUser, onDataUpdate }) => {
  const [saveLoading, setSaveLoading] = useState(false);
  const [brazilStates, setBrazilStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [cepLoading, setCepLoading] = useState(false);

  const formatPhone = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");

    // Remove country code if present
    const number = cleaned.length === 13 && cleaned.startsWith("55")
      ? cleaned.slice(2)
      : cleaned;

    // Mobile phone with 9 digits
    const match = number.match(/^(\d{2})(\d{5})(\d{4})$/);
    if (match) {
      return `+55 (${match[1]}) ${match[2]}-${match[3]}`;
    }

    // Landline with 8 digits
    const matchFixo = number.match(/^(\d{2})(\d{4})(\d{4})$/);
    if (matchFixo) {
      return `+55 (${matchFixo[1]}) ${matchFixo[2]}-${matchFixo[3]}`;
    }

    return phone;
  };

  const cleanPhoneFromDB = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.startsWith("55") && cleaned.length === 13) {
      return cleaned.slice(2);
    }
    return cleaned;
  };

  const form = useFormik({
    initialValues: {
      userName: company?.name || "",
      companyName: company?.companyName || "",
      legalName: company?.legalName || "",
      cnpj: company?.cnpj || "",
      email: company?.email || "",
      phone: cleanPhoneFromDB(company?.phone) || "",
      avatar: company?.avatar || null,
      state: company?.address?.state || "",
      city: company?.address?.city || "",
      zipCode: company?.address?.zipCode || "",
      address: company?.address?.address || "",
      number: company?.address?.number || "",
      neighborhood: company?.address?.neighborhood || "",
      complement: company?.address?.complement || "",
      country: company?.address?.country || "BR"
    },

    validationSchema: yup.object({
      userName: yup
        .string()
        .required("Nome é obrigatório"),
      companyName: yup
        .string()
        .required("Nome da empresa é obrigatório"),
      legalName: yup
        .string()
        .required("Razão social é obrigatória"),
      cnpj: yup
        .string()
        .min(18, "CNPJ deve ter 18 caracteres")
        .max(18, "CNPJ deve ter 18 caracteres")
        .required("CNPJ é obrigatório"),
      email: yup
        .string()
        .email("Formato de email inválido.")
        .required("Email é obrigatório!"),
      phone: yup
        .string()
        .required("Telefone é obrigatório"),
      state: yup
        .string()
        .required("Estado é obrigatório"),
      city: yup
        .string()
        .required("Cidade é obrigatória"),
      zipCode: yup
        .string()
        .required("CEP é obrigatório"),
      address: yup
        .string()
        .required("Endereço é obrigatório"),
      number: yup
        .string()
        .required("Número é obrigatório"),
      neighborhood: yup
        .string()
        .required("Bairro é obrigatório"),
    }),

    onSubmit: (values) => updateCompany(values),
  });

  useEffect(() => {
    if (company) {
      form.setValues({
        userName: company.name || "",
        companyName: company.companyName || "",
        legalName: company.legalName || "",
        cnpj: company.cnpj || "",
        email: company.email || "",
        phone: cleanPhoneFromDB(company.phone) || "",
        avatar: company.avatar || null,
        state: company.address?.state || "",
        city: company.address?.city || "",
        zipCode: company.address?.zipCode || "",
        address: company.address?.address || "",
        number: company.address?.number || "",
        neighborhood: company.address?.neighborhood || "",
        complement: company.address?.complement || "",
        country: company.address?.country || "BR"
      });
    }
  }, [company]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const { data } = await axios.get(
          "https://servicodados.ibge.gov.br/api/v1/localidades/estados"
        );
        setBrazilStates(data);

        if (form.values.state) {
          getCities(form.values.state);
        }
      } catch (error) {
        console.error("Erro ao carregar estados:", error);
      }
    };

    fetchStates();
  }, []);

  useEffect(() => {
    if (form.values.state && brazilStates.length > 0) {
      const stateFound = brazilStates.find(state => state.sigla === form.values.state);
      if (stateFound) {
        getCities(form.values.state);
      }
    }
  }, [form.values.state, brazilStates]);

  const getCities = async (name) => {
    try {
      const stateFound = brazilStates.find(state => state.sigla === name);
      if (!stateFound) return;

      const { data } = await axios.get(
        `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateFound.id}/municipios`
      );

      if (data) {
        setCities(data);
      }
    } catch (error) {
      console.error("Erro ao carregar cidades:", error);
    }
  };

  const resetForm = (values) => {
    form.setValues({
      userName: values?.userName || "",
      companyName: values?.companyName || "",
      legalName: values?.legalName || "",
      email: values?.email || "",
      phone: cleanPhoneFromDB(values?.phone) || "",
      avatar: values?.avatar || null,
      state: values?.state || "",
      city: values?.city || "",
      cnpj: values?.cnpj || "",
      zipCode: values?.zipCode || "",
      address: values?.address || "",
      number: values?.number || "",
      neighborhood: values?.neighborhood || "",
      complement: values?.complement || "",
      country: values?.country || "BR"
    });
  };

  // ViaCEP integration for auto-filling address
  const handleCepBlur = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');

    if (cleanCep.length === 8) {
      await fetchCepData(cleanCep);
    }
  };

  const handleCepButtonClick = async () => {
    const cleanCep = form.values.zipCode.replace(/\D/g, '');

    if (cleanCep.length === 8) {
      await fetchCepData(cleanCep);
    } else {
      toast.error("CEP deve ter 8 dígitos");
    }
  };

  const fetchCepData = async (cleanCep) => {
    setCepLoading(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();

      if (!data.erro) {
        form.setFieldValue('address', data.logradouro);
        form.setFieldValue('neighborhood', data.bairro);
        form.setFieldValue('city', data.localidade);
        form.setFieldValue('state', data.uf);
        form.setFieldValue('country', 'BR');
        toast.success("Endereço preenchido automaticamente!");
      } else {
        toast.error("CEP não encontrado");
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error("Erro ao buscar CEP");
    } finally {
      setCepLoading(false);
    }
  };

  const formatPhoneForAPI = (phone) => {
    const cleaned = unmask(phone.trim());
    if (cleaned.startsWith('55')) {
      return `+${cleaned}`;
    }
    return `+55${cleaned}`;
  };

  const updateCompany = async (values) => {
    setSaveLoading(true);

    if (values?.avatar !== company?.avatar && values.avatar) {
      try {
        const { data } = await api.put(`/companies/companies/${company.id}`, {
          data: {
            attributes: {
              avatar: { data: values?.avatar },
              name: values.userName.trim(),
              companyName: values.companyName.trim(),
              legalName: values.legalName.trim(),
              companyPhone: formatPhoneForAPI(values.phone),
              email: values.email,
              address: {
                state: values.state,
                city: values.city,
                zipCode: values.zipCode,
                address: values.address,
                number: values.number,
                neighborhood: values.neighborhood,
                complement: values.complement,
                country: "BR"
              },
              cnpj: values.cnpj,
            },
          },
        });

        if (data) {
          updateUser(data);
          resetForm(data.data.attributes);
          toast.success("Informações atualizadas com sucesso!");
          // Call callback to update billing status
          if (onDataUpdate) {
            onDataUpdate(data.data.attributes);
          }
        }

      } catch (error) {
        console.log(error)
        toast.error("Ocorreu um erro ao tentar atualizar as informações.");
      } finally {
        setSaveLoading(false);
      }
    } else {
      try {
        const { data } = await api.put(`/companies/companies/${company.id}`, {
          data: {
            attributes: {
              name: values.userName.trim(),
              companyName: values.companyName.trim(),
              legalName: values.legalName.trim(),
              companyPhone: formatPhoneForAPI(values.phone),
              email: values.email,
              address: {
                state: values.state,
                city: values.city,
                zipCode: values.zipCode,
                address: values.address,
                number: values.number,
                neighborhood: values.neighborhood,
                complement: values.complement,
                country: "BR"
              },
              cnpj: values.cnpj,
            },
          },
        });

        if (data) {
          updateUser(data);
          resetForm(data.data.attributes);
          toast.success("Informações atualizadas com sucesso!");
          // Call callback to update billing status
          if (onDataUpdate) {
            onDataUpdate(data.data.attributes);
          }
        }

      } catch (error) {
        console.log(error)
        toast.error("Ocorreu um erro ao tentar atualizar as informações.");
      } finally {
        setSaveLoading(false);
      }
    }
  };

  // Função para tratar upload de foto de perfil da empresa
  const handleCompanyFileInputChange = (e) => {
    setSaveLoading(true);
    const file = e.target.files[0];

    getBase64(file, 350, 350) // Reduzindo a imagem para 500x500 (ou o tamanho que preferir)
      .then((result) => {
        file["base64"] = result;
        form.setFieldValue(`avatar`, result); // Atualiza o campo do formulário

        const updateCompany = async () => {
          try {
            const { data } = await api.put(`/companies/companies/${company.id}`, {
              data: {
                attributes: {
                  avatar: { data: result },
                },
              },
            });

            if (data) {
              updateUser(data);
              resetForm(data.data.attributes);
              toast.success("Logo da empresa atualizada com sucesso!");
            }
          } catch (error) {
            toast.error(
              "Ocorreu um erro ao tentar atualizar a logo da empresa."
            );
          } finally {
            setSaveLoading(false);
          }
        };

        updateCompany();
      })
      .catch((error) => {
        if (error?.response?.status) {
          if (error.response.status !== 401) {
            if (
              error?.response?.data?.errors &&
              error?.response?.data?.errors[0]?.title
            ) {
              alert(`${error.response.data.errors[0].title}`);
            } else {
              alert("Something unexpected happened. Please try again.");
            }
          } else {
            alert("Session expired", "Please login in again.");
          }
        } else {
          alert("Something unexpected happened. Please try again.");
        }
      });
  };

  return (
    <div className="formProfile">
      <div className="containerInfoProfile">

        {/* Upload de foto de perfil da empresa */}
        <div className="containerPhotoProfile">
          <label className="uploadIcon" htmlFor="company-fupload">
            <div style={{ position: "relative" }}>
              <img
                src={iconAddPhoto}
                alt="edit"
                style={{
                  position: "absolute",
                  top: "52px",
                  left: "52px",
                  width: "40px",
                  height: "40px",
                  zIndex: 1,
                }}
              />

              <CompanyAvatar
                avatar={form.values.avatar}
                width="80px"
                height="80px"
                alt="edit"
                htmlFor="fupload"
              />
            </div>
            <input
              type="file"
              id="company-fupload"
              name="company-fupload"
              className="fupload form-control"
              accept=".jpeg, .png, .jpg"
              onChange={handleCompanyFileInputChange}
              style={{ display: "none" }}
            />
          </label>
        </div>

        <div className="groupInfoProfile">
          <div className="label">Nome da empresa:</div>
          <div className="value">{company?.companyName}</div>
        </div>

        <div className="groupInfoProfile">
          <div className="label">Localização:</div>
          <div className="value">
            {company?.address?.city && company?.address?.state
              ? `${company.address.city} - ${company.address.state}`
              : company?.address?.city || company?.address?.state || "Não informado"}
          </div>
        </div>

        <div className="groupInfoProfile">
          <div className="label">CNPJ:</div>
          <div className="value">{company?.cnpj}</div>
        </div>

        <div className="groupInfoProfile">
          <div className="label">Email:</div>
          <div className="value">{company?.email}</div>
        </div>

        <div className="groupInfoProfile">
          <div className="label">Telefone:</div>
          <div className="value">{formatPhone(company?.phone)}</div>
        </div>
      </div>
      <div className="containerForm">
        <TextInput
          label="Seu nome"
          value={form.values.userName}
          onChange={(val) => form.setFieldValue("userName", val)}
          placeholder="Seu nome"
          error={form.errors.userName}
        />

        <TextInput
          label="Nome da empresa"
          value={form.values.companyName}
          onChange={(val) => form.setFieldValue("companyName", val)}
          placeholder="Nome da empresa"
          error={form.errors.companyName}
        />

        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ flex: 1 }}>
            <TextInput
              label="Razão Social"
              value={form.values.legalName}
              onChange={(val) => form.setFieldValue("legalName", val)}
              placeholder="Razão Social"
              error={form.errors.legalName}
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextInput
              label="CNPJ"
              value={form.values.cnpj}
              onChange={(val) => form.setFieldValue("cnpj", val)}
              placeholder="CNPJ"
              error={form.errors.cnpj}
              maskType="cnpj"
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ flex: 1 }}>
            <TextInput
              label="Telefone"
              value={form.values.phone}
              onChange={(val) => form.setFieldValue("phone", val)}
              placeholder="Telefone"
              error={form.errors.phone}
              maskType="phone"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextInput
              label="Email"
              value={form.values.email}
              onChange={(val) => form.setFieldValue("email", val)}
              placeholder="Email"
              error={form.errors.email}
            />
          </div>
        </div>

        <div className="cepGroup">
          <div className="cepInputWrapper">
            <TextInput
              label="CEP"
              value={form.values.zipCode}
              onChange={(val) => form.setFieldValue("zipCode", val)}
              onBlur={() => handleCepBlur(form.values.zipCode)}
              placeholder="CEP"
              error={form.errors.zipCode}
              maskType="cep"
            />
          </div>
          <div className="cepButtonWrapper">
            <ButtonSecondary
              text={cepLoading ? 'Consultando...' : 'Consultar CEP'}
              onClick={handleCepButtonClick}
              disabled={cepLoading || !form.values.zipCode}
              loading={cepLoading}
              fontSize='14px'
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ flex: 2 }}>
            <TextInput
              label="Endereço"
              value={form.values.address}
              onChange={(val) => form.setFieldValue("address", val)}
              placeholder="Endereço"
              error={form.errors.address}
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextInput
              label="Número"
              value={form.values.number}
              onChange={(val) => form.setFieldValue("number", val)}
              placeholder="Número"
              error={form.errors.number}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ flex: 1 }}>
            <TextInput
              label="Complemento"
              value={form.values.complement}
              onChange={(val) => form.setFieldValue("complement", val)}
              placeholder="Complemento (opcional)"
              error={form.errors.complement}
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextInput
              label="Bairro"
              value={form.values.neighborhood}
              onChange={(val) => form.setFieldValue("neighborhood", val)}
              placeholder="Bairro"
              error={form.errors.neighborhood}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ flex: 1 }}>
            <InputAutoComplete
              label="Estado"
              placeholder="Selecione seu estado"
              options={brazilStates || []}
              value={
                brazilStates?.find((state) => {
                  return state.sigla === (form.values.state || company?.address?.state || "");
                }) || null
              }
              getOptionLabel={(option) => option.nome || ""}
              onChange={(event, newValue) => {
                const selectedValue = newValue?.sigla?.trim() || "";
                form.setFieldValue("state", selectedValue);
                form.setFieldValue("city", ""); // Limpar cidade quando mudar o estado
                if (selectedValue) {
                  getCities(selectedValue);
                }
              }}
              error={form.errors.state}
            />
          </div>
          <div style={{ flex: 1 }}>
            <InputAutoComplete
              label="Cidade"
              placeholder="Digite sua cidade"
              options={cities || []}
              value={
                cities?.find((city) => {
                  const cleanText = (str) => {
                    if (!str) return "";
                    return str
                      .toString()
                      .normalize("NFD")
                      .replace(/[\u0300-\u036f]/g, "")
                      .toLowerCase()
                      .replace(/[.,\-\s]/g, "")
                      .trim();
                  };

                  return city && city.nome
                    ? cleanText(city.nome) === cleanText(form.values.city)
                    : false;
                }) || null
              }
              getOptionLabel={(option) => option.nome || ""}
              onChange={(event, newValue) => {
                const selectedValue = newValue?.nome?.trim() || "";
                form.setFieldValue("city", selectedValue);
              }}
              disabled={!form.values.state}
              error={form.errors.city}
            />
          </div>
        </div>

        <div
          className={`buttonClientProfile ${saveLoading && "loading"}`}
          onClick={form.submitForm}
        >
          {saveLoading ? "Salvando" : "Salvar Alterações"}
        </div>
      </div>

      {saveLoading && (
        <div className="globalSpinnerOverlay">
          <div className="spinner" />
        </div>
      )}
    </div>
  );
};

export default FormProfile;

